import C from "@/modules/dashboard/Dashboard.module.css";

import { WButton } from "@/components/button/WButton.tsx";
import { WCarousel } from "@/components/carousel/WCarousel.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { getResourceCreateLink } from "@/modules/api/resourceCreateLinks.ts";
import {
    getResourceDescription,
    getResourceIconClass,
    getResourceParent,
    getResourceTypeName,
    resourceTypes,
    resourceTypesEnabledAtom,
    type ResourceType,
} from "@/modules/api/resourceTypes.tsx";
import { cn } from "@/utils/classNames.ts";
import { Trans } from "@lingui/react/macro";
import { Link as RouterLink } from "@tanstack/react-router";
import { useAtomValue } from "jotai/react";
import type { Settings } from "react-slick";

export function CoreServices({ more }: { more: boolean }) {
    //#region Hooks
    const enabled = useAtomValue(resourceTypesEnabledAtom);
    //#endregion

    const createOptions = resourceTypes.filter(
        (type) =>
            // BA has separate block in dashboard
            type !== "billing_account" && enabled[type] && getResourceCreateLink(type) !== undefined,
    );

    const settings: Settings = {
        dots: true,
        infinite: false,
        customPaging: () => <div className="dot" />,
    };

    const createLinks = createOptions.map((type: ResourceType) => <CreateLink key={type} type={type} more={more} />);

    return (
        <div className={cn(C.CreateLinks, !more && C.grid)}>
            {more ? (
                <WCarousel settings={settings} slideWidth={260} slideClassName={C.CreateSlider} showArrowsMin={3}>
                    {createLinks}
                </WCarousel>
            ) : (
                createLinks
            )}
        </div>
    );
}

function CreateLink({ type, more }: { type: ResourceType; more?: boolean }) {
    const linkProps = getResourceCreateLink(type);
    if (!linkProps) {
        return null;
    }

    const icon = (
        <div className={C.Icon}>
            <MaskIcon className={cn(getResourceIconClass(type), C.Mask)} />
        </div>
    );
    const title = <div className={C.Title}>{getResourceTypeName(type)}</div>;
    const parent = <div className={cn(C.Module, "color-muted", "font-size-small")}>{getResourceParent(type)}</div>;

    return more ? (
        <div className={cn(C.CreateLink, C.wide)}>
            {icon}
            <div className={C.Content}>
                {parent}
                {title}
                <div className={cn(C.Description, "color-muted", "font-size-small")}>
                    {getResourceDescription(type)}
                </div>
            </div>

            <WButton
                color="primary"
                icon="jp-arrow-thin-right-icon"
                iconSide="right"
                variant="ghost"
                size="bar"
                action={linkProps}
            >
                <Trans>Create new</Trans>
            </WButton>
        </div>
    ) : (
        <RouterLink {...linkProps}>
            <div className={cn(C.CreateLink, C.Hoverable)}>
                {icon}
                <div className={C.Content}>
                    {title}
                    {parent}
                </div>

                <div className={C.Btn}>
                    <MaskIcon className="jp-arrow-thin-right-icon size-0.75rem" />
                </div>
            </div>
        </RouterLink>
    );
}
